import React from "react";
import { INQUIRY_TYPES, SMART_VIEWS, TYPE_TONE } from "../constants";
import {
  classNames,
  formatRelative,
  statusOf,
  urgencyFor,
  urgencyTone,
} from "./utils";

function StatusPill({ inquiry }) {
  const status = statusOf(inquiry);
  const map = {
    new: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    replied: "bg-indigo-50 text-indigo-700 ring-indigo-200",
    closed: "bg-zinc-100 text-zinc-600 ring-zinc-200",
  };
  const label = { new: "New", replied: "Replied", closed: "Closed" }[status];
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1",
        map[status]
      )}
    >
      {label}
    </span>
  );
}

function TypeBadge({ type }) {
  const tone = TYPE_TONE[type] || TYPE_TONE.Other;
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1",
        tone.chip
      )}
    >
      <span className={classNames("inline-block h-1.5 w-1.5 rounded-full", tone.dot)} />
      {type || "Other"}
    </span>
  );
}

function UrgencyAccent({ inquiry }) {
  const id = urgencyFor(inquiry);
  if (!id) return <span className="block w-[3px]" />;
  const tone = urgencyTone(id);
  return <span className={classNames("block h-full w-[3px]", tone.bar)} />;
}

function LastActivity({ inquiry }) {
  if (inquiry.closed) {
    return <span className="text-xs text-zinc-500">Closed {formatRelative(inquiry.updatedAt)}</span>;
  }
  const responses = Array.isArray(inquiry.responses) ? inquiry.responses : [];
  if (responses.length) {
    const last = responses[responses.length - 1];
    return (
      <span className="truncate text-xs text-zinc-500" title={last?.responder}>
        {last?.responder ? `Replied by ${last.responder}` : "Replied"} ·{" "}
        {formatRelative(last?.respondedAt)}
      </span>
    );
  }
  return (
    <span className="text-xs text-zinc-500">
      Waiting · received {formatRelative(inquiry.createdAt)}
    </span>
  );
}

function RowActions({ inquiry, onMarkReplied, onToggleClosed, onChangeType }) {
  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <select
        value={inquiry.type || "Other"}
        onChange={(e) => onChangeType?.(inquiry._id, e.target.value)}
        className="rounded-md border border-zinc-200 bg-white px-1.5 py-1 text-[11px] text-zinc-700 focus:border-indigo-500 focus:outline-none"
      >
        {INQUIRY_TYPES.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      {!inquiry.replied && !inquiry.closed ? (
        <button
          type="button"
          onClick={() => onMarkReplied?.(inquiry._id)}
          className="rounded-md border border-indigo-200 bg-indigo-50 px-1.5 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100"
          title="Mark as replied without sending"
        >
          Mark replied
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => onToggleClosed?.(inquiry._id, !inquiry.closed)}
        className={classNames(
          "rounded-md px-1.5 py-1 text-[11px] font-medium",
          inquiry.closed
            ? "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
            : "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
        )}
        title={inquiry.closed ? "Reopen" : "Close inquiry"}
      >
        {inquiry.closed ? "Reopen" : "Close"}
      </button>
    </div>
  );
}

function Row({ inquiry, selected, onSelect, onMarkReplied, onToggleClosed, onChangeType }) {
  return (
    <div
      onClick={() => onSelect?.(inquiry._id)}
      className={classNames(
        "relative flex cursor-pointer items-stretch gap-3 border-b border-zinc-100 bg-white py-2 pl-0 pr-3 transition-colors",
        selected ? "bg-indigo-50/60" : "hover:bg-zinc-50"
      )}
    >
      <UrgencyAccent inquiry={inquiry} />
      <div className="flex min-w-0 flex-1 items-center gap-3 py-1">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <p className="truncate text-sm font-semibold text-zinc-900">
              {inquiry.name || "Unnamed"}
            </p>
            <TypeBadge type={inquiry.type} />
            <StatusPill inquiry={inquiry} />
          </div>
          <p className="mt-0.5 truncate text-xs text-zinc-500">
            {inquiry.email || "no email"}
            {inquiry.phone ? <span className="mx-1 text-zinc-300">·</span> : null}
            {inquiry.phone || ""}
          </p>
          <p className="mt-1 line-clamp-1 text-xs text-zinc-700">
            {inquiry.subject ? (
              <span className="font-medium text-zinc-800">{inquiry.subject}: </span>
            ) : null}
            {inquiry.message || <span className="italic text-zinc-400">(no message)</span>}
          </p>
        </div>

        <div className="flex w-44 shrink-0 flex-col items-end gap-1">
          <span className="text-xs text-zinc-500" title={new Date(inquiry.createdAt).toString()}>
            {formatRelative(inquiry.createdAt)}
          </span>
          <LastActivity inquiry={inquiry} />
        </div>

        <div className="shrink-0">
          <RowActions
            inquiry={inquiry}
            onMarkReplied={onMarkReplied}
            onToggleClosed={onToggleClosed}
            onChangeType={onChangeType}
          />
        </div>
      </div>
    </div>
  );
}

export default function InquiryLedger({
  inquiries,
  loading,
  view,
  search,
  onSearchChange,
  selectedId,
  onSelect,
  onMarkReplied,
  onToggleClosed,
  onChangeType,
}) {
  const viewMeta = SMART_VIEWS.find((v) => v.id === view);
  const total = inquiries.length;

  return (
    <section className="flex min-w-0 flex-1 flex-col bg-zinc-50/60">
      <header className="flex flex-wrap items-center gap-3 border-b border-zinc-200 bg-white px-4 py-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
            View
          </p>
          <h2 className="text-sm font-semibold text-zinc-900">
            {viewMeta?.label || "Inquiries"}{" "}
            <span className="ml-1 text-xs font-normal text-zinc-500">{total}</span>
          </h2>
        </div>
        <div className="ml-auto flex min-w-[240px] flex-1 items-center gap-2">
          <input
            type="text"
            data-inq-search
            value={search}
            onChange={(event) => onSearchChange?.(event.target.value)}
            placeholder="Search name, email, phone, subject, message…"
            className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-sm placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          />
          {search ? (
            <button
              type="button"
              onClick={() => onSearchChange?.("")}
              className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] text-zinc-600 hover:bg-zinc-50"
            >
              Clear
            </button>
          ) : null}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        {loading && !inquiries.length ? (
          <div className="flex h-full items-center justify-center p-10 text-sm text-zinc-500">
            Loading inquiries…
          </div>
        ) : !inquiries.length ? (
          <EmptyState view={view} />
        ) : (
          <div>
            {inquiries.map((inquiry) => (
              <Row
                key={inquiry._id}
                inquiry={inquiry}
                selected={selectedId === inquiry._id}
                onSelect={onSelect}
                onMarkReplied={onMarkReplied}
                onToggleClosed={onToggleClosed}
                onChangeType={onChangeType}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function EmptyState({ view }) {
  const copy = {
    today: {
      title: "Nothing new today — yet.",
      body: "Inquiries received today will show up here the moment they land.",
    },
    awaiting: {
      title: "No inquiries waiting on a reply.",
      body: "You're fully caught up. Nice.",
    },
    replied: {
      title: "No replied inquiries yet.",
      body: "Reply to something from Awaiting reply and it'll land here.",
    },
    closed: {
      title: "No closed inquiries.",
      body: "Closed inquiries are archived but kept for reference.",
    },
    all: {
      title: "No inquiries at all.",
      body: "New inbound messages appear here automatically.",
    },
  }[view] || { title: "No results", body: "Try clearing filters or search." };

  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 p-12 text-center">
      <p className="text-sm font-semibold text-zinc-700">{copy.title}</p>
      <p className="max-w-xs text-xs text-zinc-500">{copy.body}</p>
    </div>
  );
}
